import type { DayPoint } from './types';

/**
 * A day-by-day bar chart.
 *
 * ## The empty days are the point
 *
 * The server returns every day in the window, including the ones with nothing
 * in them, and this draws them. A chart built only from days that had plays
 * puts four bars evenly across a month and reads as steady interest, when what
 * happened was four plays a fortnight apart.
 *
 * ## Scaled to its own peak
 *
 * Not to a fixed ceiling. Somebody with eleven plays should see the shape of
 * their eleven plays, not a flat line at the bottom of a chart built for
 * thousands. The peak is labelled so the height still means something.
 */
export function BarSeries({ points, height = 120 }: { points: DayPoint[]; height?: number }) {
  const peak = Math.max(1, ...points.map((p) => p.plays));
  const total = points.reduce((sum, p) => sum + p.plays, 0);

  return (
    <div className="flex flex-col gap-2" data-bar-series>
      <div className="flex items-end gap-[3px]" style={{ height }}>
        {points.map((point) => (
          <span
            key={point.date}
            title={`${point.plays} ${point.plays === 1 ? 'play' : 'plays'} on ${label(point.date)}`}
            className={`min-w-px flex-1 rounded-[2px] ${point.plays > 0 ? 'bg-accent' : 'bg-[#1a1a38]'}`}
            /* A floor of 2px so a day with nothing is still a day, rather than
               a gap that reads as missing data. */
            style={{ height: `${Math.max(2, (point.plays / peak) * 100)}%` }}
          />
        ))}
      </div>

      <div className="flex justify-between font-mono text-[10px] text-ghost">
        <span>{points.length > 0 ? label(points[0]!.date) : ''}</span>
        <span>
          {total.toLocaleString()} total · peak {peak.toLocaleString()}
        </span>
        <span>today</span>
      </div>
    </div>
  );
}

/** `12 Aug`, which is short enough for an axis and unambiguous. */
function label(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}
